import { useState, useEffect, useMemo, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  History, Search, X, FileText, FileSpreadsheet,
  DollarSign, Banknote, Eye, Building2, Calendar,
  CreditCard, Receipt, Users, ChevronRight, ChevronDown, Download, Undo2, Send
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import { gastosApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';
import NotificarPagoProveedorModal from '../../components/NotificarPagoProveedorModal';
import DraggableModal from '../../components/DraggableModal';

const medioPagoBadges = {
  transferencia: { bg: 'bg-blue-100', text: 'text-blue-700', label: 'Transferencia' },
  efectivo: { bg: 'bg-green-100', text: 'text-green-700', label: 'Efectivo' },
  cheque: { bg: 'bg-yellow-100', text: 'text-yellow-700', label: 'Cheque' },
  tarjeta: { bg: 'bg-purple-100', text: 'text-purple-700', label: 'Tarjeta' },
  sinpe: { bg: 'bg-indigo-100', text: 'text-indigo-700', label: 'SINPE' },
  nota_credito: { bg: 'bg-red-100', text: 'text-red-700', label: '⚡ Anulación por NC' },
  otro: { bg: 'bg-gray-100', text: 'text-gray-700', label: 'Otro' }
};

export default function HistoricoPagos() {
  const [pagos, setPagos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchProveedor, setSearchProveedor] = useState('');
  const [selectedProveedorId, setSelectedProveedorId] = useState(null);
  const { searchRef: providerNameRef, buttonsRef: exportButtonsRef } = useToolbarResponsive([selectedProveedorId]);

  // Limpiar selección de pago al cambiar de proveedor (no aplica a otro proveedor)
  useEffect(() => { setSelectedPagoForNotif(null); }, [selectedProveedorId]);

  // Modal desaplicar
  const [showDesaplicarModal, setShowDesaplicarModal] = useState(false);
  const [selectedPago, setSelectedPago] = useState(null);
  // Selección checkbox para enviar notificación (independiente del openDesaplicarModal)
  const [selectedPagoForNotif, setSelectedPagoForNotif] = useState(null);
  const [notifInfo, setNotifInfo] = useState(null); // info para abrir el modal
  const { currentEmisor } = useAuthStore();

  // FE de compra a ver en modal solo lectura (facturaId) desde "Facturas Pagadas"
  const [verFacturaId, setVerFacturaId] = useState(null);
  // Modal detalle
  const [showDetalleModal, setShowDetalleModal] = useState(false);
  const [detallePago, setDetallePago] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);
  const [comprobanteUrl, setComprobanteUrl] = useState(null);
  const [comprobanteType, setComprobanteType] = useState(null);

  // Exportando
  const [exportando, setExportando] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();
  const autoAbiertoRef = useRef(false);

  useEffect(() => {
    loadPagos();
  }, []);

  // Al llegar desde una notificación (?parentId=X): seleccionar el proveedor y abrir
  // el detalle de ese pago exacto. La pantalla lista pagos PADRE (pagos_cxp_parent),
  // por eso usamos parentId (no el id del pago hijo). Se acepta pagoId por compat.
  useEffect(() => {
    if (loading || autoAbiertoRef.current) return;
    const targetId = searchParams.get('parentId') || searchParams.get('pagoId');
    if (!targetId) return;
    const pago = pagos.find(p => String(p.id) === String(targetId));
    if (pago) {
      autoAbiertoRef.current = true;
      // Seleccionar el proveedor al que se le pagó (la lista agrupa por cédula)
      setSelectedProveedorId(pago.proveedorIdentificacion || 'sin-proveedor');
      openDetalleModal(pago);
      const next = new URLSearchParams(searchParams);
      next.delete('parentId');
      next.delete('pagoId');
      setSearchParams(next, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [loading, pagos, searchParams]);

  const loadPagos = async () => {
    try {
      setLoading(true);
      const response = await gastosApi.listPagosParent({ limit: 1000 });
      setPagos(response.data.data || []);
    } catch (error) {
      console.error('Error loading pagos:', error);
      toast.error('Error al cargar historial');
    } finally {
      setLoading(false);
    }
  };

  // Agrupar pagos por proveedor
  const proveedoresConPagos = useMemo(() => {
    const proveedorMap = new Map();

    pagos.forEach(p => {
      const proveedorKey = p.proveedorIdentificacion || 'sin-proveedor';

      if (!proveedorMap.has(proveedorKey)) {
        proveedorMap.set(proveedorKey, {
          id: proveedorKey,                    // key agrupador (cédula/identificación)
          proveedorIdReal: p.proveedorId || null, // id numérico real (tabla proveedores)
          nombre: p.proveedorNombre || 'Proveedor sin nombre',
          identificacion: p.proveedorIdentificacion || '',
          pagos: [],
          totalCRC: 0,
          totalUSD: 0,
          cantidadCRC: 0,
          cantidadUSD: 0
        });
      }

      const proveedor = proveedorMap.get(proveedorKey);
      proveedor.pagos.push(p);

      const monto = parseFloat(p.montoTotal) || 0;
      if (p.moneda === 'USD') {
        proveedor.totalUSD += monto;
        proveedor.cantidadUSD++;
      } else {
        proveedor.totalCRC += monto;
        proveedor.cantidadCRC++;
      }
    });

    // Ordenar alfabéticamente por nombre del proveedor
    return Array.from(proveedorMap.values())
      .sort((a, b) => (a.nombre || '').localeCompare(b.nombre || '', 'es', { sensitivity: 'base' }));
  }, [pagos]);

  // Filtrar proveedores por busqueda
  const proveedoresFiltrados = useMemo(() => {
    if (!searchProveedor.trim()) return proveedoresConPagos;
    const search = searchProveedor.toLowerCase();
    return proveedoresConPagos.filter(p =>
      p.nombre.toLowerCase().includes(search) ||
      p.identificacion.toLowerCase().includes(search)
    );
  }, [proveedoresConPagos, searchProveedor]);

  // Proveedor seleccionado
  const proveedorSeleccionado = useMemo(() => {
    if (!selectedProveedorId) return null;
    return proveedoresConPagos.find(p => p.id === selectedProveedorId);
  }, [proveedoresConPagos, selectedProveedorId]);

  // Seleccionar primer proveedor por defecto
  useEffect(() => {
    if (proveedoresFiltrados.length > 0 && !selectedProveedorId) {
      setSelectedProveedorId(proveedoresFiltrados[0].id);
    }
  }, [proveedoresFiltrados, selectedProveedorId]);

  const formatMoney = (amount, moneda = 'CRC') => {
    const num = parseFloat(amount) || 0;
    if (moneda === 'USD') {
      return `$ ${num.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    }
    return `₡ ${num.toLocaleString('es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return dayjs(dateStr).format('DD/MM/YYYY');
  };

  const getMedioPagoBadge = (medio) => {
    const m = medioPagoBadges[medio] || medioPagoBadges.otro;
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${m.bg} ${m.text}`}>
        {m.label}
      </span>
    );
  };

  const openDesaplicarModal = (pago) => {
    setSelectedPago(pago);
    setShowDesaplicarModal(true);
  };

  const openDetalleModal = async (pago) => {
    setDetallePago(null);
    setComprobanteUrl(null);
    setComprobanteType(null);
    setShowDetalleModal(true);
    setLoadingDetalle(true);

    try {
      const response = await gastosApi.getPagoParent(pago.id);
      setDetallePago(response.data.data);

      if (response.data.data.comprobanteStoragePath) {
        try {
          const compResponse = await gastosApi.getPagoParentComprobante(pago.id);
          const blob = compResponse.data;
          const url = URL.createObjectURL(blob);
          setComprobanteUrl(url);
          setComprobanteType(blob.type);
        } catch (err) {
          console.error('Error cargando comprobante:', err);
        }
      }
    } catch (error) {
      console.error('Error cargando detalle:', error);
      toast.error('Error al cargar detalle del pago');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const closeDetalleModal = () => {
    if (comprobanteUrl) {
      URL.revokeObjectURL(comprobanteUrl);
    }
    setShowDetalleModal(false);
    setDetallePago(null);
    setComprobanteUrl(null);
    setComprobanteType(null);
  };

  const handleDesaplicar = async (motivo) => {
    if (!selectedPago || !motivo.trim()) {
      toast.error('Debe ingresar un motivo');
      return;
    }

    try {
      await gastosApi.reversarPagoParent(selectedPago.id, motivo);
      toast.success(`Pago revertido (${selectedPago.cantidadFacturas || 1} factura(s))`);
      setShowDesaplicarModal(false);
      loadPagos();
    } catch (error) {
      console.error('Error desaplicando:', error);
      toast.error(error.response?.data?.error || 'Error al desaplicar pago');
    }
  };

  const handleExportExcel = async () => {
    if (!proveedorSeleccionado) return;
    try {
      setExportando(true);
      const proveedorId = proveedorSeleccionado.pagos[0]?.proveedorId;
      const response = await gastosApi.exportarHistorialPagos('excel', { proveedorId });

      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Historial_${proveedorSeleccionado.nombre.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('Excel exportado');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('Error al exportar');
    } finally {
      setExportando(false);
    }
  };

  const handleExportPDF = async () => {
    if (!proveedorSeleccionado) return;
    try {
      setExportando(true);
      const proveedorId = proveedorSeleccionado.pagos[0]?.proveedorId;
      const response = await gastosApi.exportarHistorialPagos('pdf', { proveedorId });

      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `Historial_${proveedorSeleccionado.nombre.replace(/\s+/g, '_')}_${dayjs().format('YYYY-MM-DD')}.pdf`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('PDF exportado');
    } catch (error) {
      console.error('Error exportando:', error);
      toast.error('Error al exportar');
    } finally {
      setExportando(false);
    }
  };

  // Contar facturas totales del proveedor
  const getTotalFacturas = (proveedor) => {
    return proveedor.pagos.reduce((sum, p) => sum + (p.cantidadFacturas || 1), 0);
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 outline-total">
      {/* Layout Master-Detail */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[var(--alto-contenido)]">

        {/* Panel Izquierdo - Lista de Proveedores */}
        <div className="lg:col-span-1 bg-white rounded-xl border flex flex-col overflow-hidden">
          {/* Busqueda */}
          <div className="p-3 border-b">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar proveedor..."
                value={searchProveedor}
                onChange={(e) => setSearchProveedor(e.target.value)}
                className="w-full pl-9 pr-4 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>
          </div>

          {/* Lista de Proveedores */}
          <div className="flex-1 overflow-y-auto">
            {proveedoresFiltrados.length === 0 ? (
              <div className="p-8 text-center text-gray-500">
                <Building2 className="h-12 w-12 mx-auto text-gray-300 mb-2" />
                <p>No hay proveedores con pagos registrados</p>
              </div>
            ) : (
              <div className="divide-y">
                {proveedoresFiltrados.map((proveedor) => (
                  <button
                    key={proveedor.id}
                    onClick={() => setSelectedProveedorId(proveedor.id)}
                    className={`w-full p-3 text-left hover:bg-gray-50 transition-colors ${
                      selectedProveedorId === proveedor.id ? 'bg-blue-50 border-l-4 border-l-blue-500' : ''
                    }`}
                  >
                    <div className="flex items-start justify-between gap-2">
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 truncate">{proveedor.nombre}</p>
                        <p className="text-xs text-gray-500">{proveedor.identificacion}</p>
                      </div>
                      <span className="flex-shrink-0 px-1.5 py-0.5 bg-blue-100 text-blue-700 text-xs font-medium rounded">
                        {proveedor.pagos.length}
                      </span>
                    </div>

                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="p-3 border-t bg-gray-50">
            <p className="text-xs text-gray-500">{proveedoresFiltrados.length} proveedores</p>
          </div>
        </div>

        {/* Panel Derecho - Historial del Proveedor */}
        <div className="lg:col-span-2 bg-white rounded-xl border flex flex-col overflow-hidden">
          {!proveedorSeleccionado ? (
            <div className="flex-1 flex items-center justify-center text-gray-500">
              <div className="text-center">
                <History className="h-16 w-16 mx-auto text-gray-300 mb-3" />
                <p className="font-medium">Seleccione un proveedor</p>
                <p className="text-sm">para ver su historial de pagos</p>
              </div>
            </div>
          ) : (
            <>
              {/* Header del proveedor — responsive: nombre absolute izq, botones centrados */}
              <div className="p-4 border-b bg-white relative">
                <div ref={providerNameRef} className="absolute left-4 top-1/2 -translate-y-1/2 flex items-center gap-3 z-10">
                  <div className="p-3 bg-blue-100 rounded-xl">
                    <Building2 className="h-6 w-6 text-blue-600" />
                  </div>
                  <div>
                    <h2 className="text-lg font-bold text-gray-900">{proveedorSeleccionado.nombre}</h2>
                    <p className="text-sm text-gray-500">{proveedorSeleccionado.identificacion}</p>
                  </div>
                </div>
                {/* Botones de Exportar — centrados al header (responsive con hook) */}
                <div ref={exportButtonsRef} className="flex items-center justify-center gap-2 py-2">
                  <button
                    onClick={() => {
                      if (!selectedPagoForNotif) return;
                      setNotifInfo({
                        // El .id del grupo es la cédula (string); el real está en pagos[0].proveedorId
                        proveedorId: proveedorSeleccionado.proveedorIdReal || proveedorSeleccionado.pagos[0]?.proveedorId,
                        proveedorNombre: proveedorSeleccionado.nombre,
                        parentId: selectedPagoForNotif.id,
                        fechaPago: selectedPagoForNotif.fechaPago,
                        montoTotal: selectedPagoForNotif.montoTotal,
                        moneda: selectedPagoForNotif.moneda,
                        medioPago: selectedPagoForNotif.medioPago,
                        banco: selectedPagoForNotif.banco,
                        numeroComprobante: selectedPagoForNotif.numeroComprobante,
                        comprobanteFilename: selectedPagoForNotif.comprobanteFilename
                      });
                    }}
                    disabled={!selectedPagoForNotif}
                    className="flex items-center gap-2 px-3 py-2 border-2 border-violet-500 text-violet-600 rounded-lg hover:bg-violet-50 disabled:opacity-40 disabled:cursor-not-allowed transition-colors text-sm font-medium"
                    title={selectedPagoForNotif ? 'Enviar notificación de este pago al proveedor' : 'Seleccione un pago de la tabla'}
                  >
                    <Send size={16} />
                    Notificar Pago
                  </button>
                  <button
                    onClick={handleExportExcel}
                    disabled={exportando}
                    className="flex items-center gap-2 px-3 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    <FileSpreadsheet size={16} />
                    Excel
                  </button>
                  <button
                    onClick={handleExportPDF}
                    disabled={exportando}
                    className="flex items-center gap-2 px-3 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50 transition-colors text-sm font-medium"
                  >
                    <Download size={16} />
                    PDF
                  </button>
                </div>
              </div>

              {/* Cards de Totales */}
              <div className="p-4 border-b">
                <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                  {/* CRC */}
                  <div className="bg-white rounded-lg border border-green-300 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Banknote className="h-4 w-4 text-green-600" />
                      <span className="text-xs font-medium text-green-800">Colones</span>
                    </div>
                    <p className="text-lg font-bold text-green-700 ">
                      {formatMoney(proveedorSeleccionado.totalCRC, 'CRC')}
                    </p>
                    <p className="text-xs text-green-600">{proveedorSeleccionado.cantidadCRC} pagos</p>
                  </div>

                  {/* USD */}
                  <div className="bg-white rounded-lg border border-blue-300 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="h-4 w-4 text-blue-600" />
                      <span className="text-xs font-medium text-blue-800">Dolares</span>
                    </div>
                    <p className="text-lg font-bold text-blue-700 ">
                      {formatMoney(proveedorSeleccionado.totalUSD, 'USD')}
                    </p>
                    <p className="text-xs text-blue-600">{proveedorSeleccionado.cantidadUSD} pagos</p>
                  </div>

                  {/* Total Pagos */}
                  <div className="bg-white rounded-lg border border-purple-300 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <Receipt className="h-4 w-4 text-purple-600" />
                      <span className="text-xs font-medium text-purple-800">Total Pagos</span>
                    </div>
                    <p className="text-lg font-bold text-purple-700 ">
                      {proveedorSeleccionado.pagos.length}
                    </p>
                    <p className="text-xs text-purple-600">Transacciones</p>
                  </div>

                  {/* Facturas Pagadas */}
                  <div className="bg-white rounded-lg border border-amber-300 p-3">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-amber-600" />
                      <span className="text-xs font-medium text-amber-800">Facturas</span>
                    </div>
                    <p className="text-lg font-bold text-amber-700 ">
                      {getTotalFacturas(proveedorSeleccionado)}
                    </p>
                    <p className="text-xs text-amber-600">Pagadas</p>
                  </div>
                </div>
              </div>

              {/* Tabla de Pagos */}
              <div className="flex-1 overflow-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 sticky top-0">
                    <tr>
                      <th className="w-10 px-3 py-3"></th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Fecha</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Facturas</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Medio</th>
                      <th className="px-4 py-3 text-left text-xs font-semibold text-gray-600">Referencia</th>
                      <th className="px-4 py-3 text-right text-xs font-semibold text-gray-600">Monto</th>
                      <th className="px-4 py-3 text-center text-xs font-semibold text-gray-600">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {proveedorSeleccionado.pagos
                      .sort((a, b) => new Date(b.fechaPago) - new Date(a.fechaPago))
                      .map((pago) => (
                          <tr key={pago.id} className={`hover:bg-gray-50 ${selectedPagoForNotif?.id === pago.id ? 'bg-violet-50' : ''} ${pago.medioPago === 'nota_credito' ? 'bg-red-50/40 border-l-2 border-l-red-400' : ''}`}>
                            <td className="px-3 py-3 text-center">
                              <input
                                type="radio"
                                name="pago-notif"
                                checked={selectedPagoForNotif?.id === pago.id}
                                onChange={() => setSelectedPagoForNotif(pago)}
                                onClick={(e) => e.stopPropagation()}
                                className="w-4 h-4 text-violet-600 focus:ring-violet-500"
                                title="Seleccionar para notificar al proveedor"
                              />
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center gap-2">
                                <Calendar className="h-4 w-4 text-gray-400" />
                                <span className="text-sm text-gray-900">{formatDate(pago.fechaPago)}</span>
                              </div>
                            </td>
                            <td className="px-4 py-3 text-center">
                              <span className="inline-flex items-center gap-1 px-2.5 py-1 bg-amber-100 text-amber-700 rounded-full text-xs font-semibold">
                                <FileText className="h-3 w-3" />
                                {pago.cantidadFacturas || 1}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              {getMedioPagoBadge(pago.medioPago)}
                            </td>
                            <td className="px-4 py-3 text-sm text-gray-500">
                              {pago.numeroComprobante || '-'}
                            </td>
                            <td className="px-4 py-3 text-right">
                              <span className={`text-sm font-bold ${
                                pago.moneda === 'USD' ? 'text-blue-600' : 'text-gray-600'
                              }`}>
                                {formatMoney(pago.montoTotal, pago.moneda)}
                              </span>
                            </td>
                            <td className="px-4 py-3">
                              <div className="flex items-center justify-center gap-2">
                                <button
                                  onClick={() => openDetalleModal(pago)}
                                  className="p-1.5 rounded transition-colors text-blue-500 hover:bg-blue-50"
                                  title="Ver detalle del pago"
                                >
                                  <Eye className="h-5 w-5" />
                                </button>
                                <button
                                  onClick={() => openDesaplicarModal(pago)}
                                  className="p-1.5 text-orange-500 hover:bg-orange-50 rounded transition-colors"
                                  title="Desaplicar pago"
                                >
                                  <Undo2 className="h-5 w-5" />
                                </button>
                              </div>
                            </td>
                          </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </>
          )}
        </div>
      </div>

      {/* Modal Desaplicar */}
      {showDesaplicarModal && selectedPago && (
        <DesaplicarModal
          pago={selectedPago}
          onClose={() => {
            setShowDesaplicarModal(false);
            setSelectedPago(null);
          }}
          onConfirm={handleDesaplicar}
        />
      )}

      {/* Modal Notificar Pago al Proveedor */}
      {notifInfo && (
        <NotificarPagoProveedorModal
          info={notifInfo}
          emisor={currentEmisor}
          onClose={() => setNotifInfo(null)}
        />
      )}

      {/* Modal Detalle del Pago */}
      {showDetalleModal && (
        <DetalleModal
          detallePago={detallePago}
          loadingDetalle={loadingDetalle}
          comprobanteUrl={comprobanteUrl}
          comprobanteType={comprobanteType}
          onClose={closeDetalleModal}
          formatMoney={formatMoney}
          formatDate={formatDate}
          getMedioPagoBadge={getMedioPagoBadge}
          onVerFactura={setVerFacturaId}
        />
      )}

      {/* Modal solo lectura para ver la factura de compra desde Facturas Pagadas */}
      {verFacturaId && (
        <VerFacturaCompraModal facturaId={verFacturaId} onClose={() => setVerFacturaId(null)} />
      )}
    </div>
  );
}

// Modal solo lectura: muestra el PDF de la factura de compra
function VerFacturaCompraModal({ facturaId, onClose }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  const [titulo, setTitulo] = useState('Factura');

  useEffect(() => {
    let objUrl = null;
    (async () => {
      try {
        setLoading(true);
        setError(false);
        try {
          const det = await gastosApi.getFactura(facturaId);
          const f = det.data?.data || det.data;
          if (f) setTitulo(`${f.proveedorNombre || f.proveedor_nombre || 'Factura'} - ${f.numeroConsecutivo || f.numero_consecutivo || f.numeroFacturaProveedor || ''}`);
        } catch { /* título opcional */ }
        const res = await gastosApi.downloadPDF(facturaId);
        const blob = new Blob([res.data], { type: 'application/pdf' });
        objUrl = URL.createObjectURL(blob);
        setPdfUrl(objUrl);
      } catch (e) {
        setError(true);
      } finally {
        setLoading(false);
      }
    })();
    return () => { if (objUrl) URL.revokeObjectURL(objUrl); };
  }, [facturaId]);

  return (
    <DraggableModal title={titulo} onClose={onClose}>
      <div className="w-full bg-gray-200" style={{ height: '75vh' }}>
        {loading ? (
          <div className="flex items-center justify-center h-full">
            <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600"></div>
          </div>
        ) : pdfUrl && !error ? (
          <iframe src={pdfUrl} className="w-full h-full" title="Factura de compra" />
        ) : (
          <div className="flex items-center justify-center h-full text-gray-500">
            No hay PDF disponible para esta factura.
          </div>
        )}
      </div>
    </DraggableModal>
  );
}

// Desaplicar Modal
function DesaplicarModal({ pago, onClose, onConfirm }) {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (motivo.trim().length < 5) {
      toast.error('El motivo debe tener al menos 5 caracteres');
      return;
    }
    setLoading(true);
    await onConfirm(motivo);
    setLoading(false);
  };

  const formatMoney = (amount, currency = 'CRC') => {
    const num = parseFloat(amount) || 0;
    return currency === 'USD'
      ? `$ ${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : `₡ ${num.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-xl">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-orange-600">Desaplicar Pago</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 bg-orange-50 border-b">
          <div className="flex items-center gap-2 text-orange-700 mb-2">
            <Undo2 size={18} />
            <span className="font-medium">Esta acción revertirá el pago</span>
          </div>
          <div className="text-sm text-orange-600 space-y-1">
            <p><strong>Proveedor:</strong> {pago.proveedorNombre}</p>
            <p><strong>Facturas:</strong> {pago.cantidadFacturas || 1}</p>
            <p><strong>Monto:</strong> {formatMoney(pago.montoTotal, pago.moneda)}</p>
            <p><strong>Fecha:</strong> {dayjs(pago.fechaPago).format('DD/MM/YYYY')}</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo de la desaplicación *
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
              placeholder="Explique el motivo..."
              rows={3}
              className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-orange-500"
              required
              minLength={5}
            />
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || motivo.trim().length < 5}
              className="px-4 py-2 border-2 border-orange-600 text-orange-600 rounded-lg hover:bg-orange-50 disabled:opacity-50 font-medium"
            >
              {loading ? 'Procesando...' : 'Desaplicar'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Detalle Modal
function DetalleModal({ detallePago, loadingDetalle, comprobanteUrl, comprobanteType, onClose, formatMoney, formatDate, getMedioPagoBadge, onVerFactura }) {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-4xl max-h-[90vh] flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b flex-shrink-0">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {detallePago?.medioPago === 'nota_credito' ? 'Aplicación de Nota de Crédito' : 'Detalle del Pago'}
            </h3>
            <p className="text-sm text-gray-500">
              {detallePago
                ? detallePago.medioPago === 'nota_credito'
                  ? `NC aplicada a ${detallePago.cantidadFacturas || 1} factura(s)`
                  : `${detallePago.cantidadFacturas || 1} factura(s) pagada(s)`
                : 'Cargando...'}
            </p>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg">
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-auto">
          {loadingDetalle ? (
            <div className="flex items-center justify-center h-64">
              <div className="animate-spin h-10 w-10 border-4 border-blue-500 border-t-transparent rounded-full"></div>
            </div>
          ) : detallePago ? (
            <>
              {/* Info del Pago */}
              <div className="p-6 border-b bg-gradient-to-r from-blue-50 to-indigo-50">
                {/* Fila 1: Fecha, Medio, Monto, Facturas */}
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm border">
                    <div className="flex items-center gap-2 mb-1">
                      <Calendar className="h-4 w-4 text-blue-600" />
                      <span className="text-xs font-medium text-gray-500">Fecha de Pago</span>
                    </div>
                    <p className="text-lg font-bold text-gray-900">{dayjs(detallePago.fechaPago).format('DD/MM/YYYY')}</p>
                    <p className="text-sm text-gray-500">{dayjs(detallePago.createdAt || detallePago.fechaPago).format('HH:mm')} hrs</p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm border">
                    <div className="flex items-center gap-2 mb-1">
                      <CreditCard className="h-4 w-4 text-purple-600" />
                      <span className="text-xs font-medium text-gray-500">Medio de Pago</span>
                    </div>
                    <div className="mt-1">{getMedioPagoBadge(detallePago.medioPago)}</div>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm border">
                    <div className="flex items-center gap-2 mb-1">
                      <DollarSign className="h-4 w-4 text-green-600" />
                      <span className="text-xs font-medium text-gray-500">{detallePago?.medioPago === 'nota_credito' ? 'Monto NC' : 'Monto del Pago'}</span>
                    </div>
                    <p className={`text-xl font-bold ${detallePago.moneda === 'USD' ? 'text-blue-600' : 'text-gray-600'}`}>
                      {formatMoney(detallePago.montoTotal, detallePago.moneda)}
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm border">
                    <div className="flex items-center gap-2 mb-1">
                      <FileText className="h-4 w-4 text-amber-600" />
                      <span className="text-xs font-medium text-gray-500">Facturas</span>
                    </div>
                    <p className="text-xl font-bold text-amber-700">{detallePago.cantidadFacturas || 1}</p>
                  </div>
                </div>

                {/* Fila 2: Registrado por, Referencia, Banco */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm border">
                    <div className="flex items-center gap-2 mb-1">
                      <Users className="h-4 w-4 text-gray-600" />
                      <span className="text-xs font-medium text-gray-500">Registrado por</span>
                    </div>
                    <p className="text-base font-medium text-gray-900">{detallePago.createdByName || 'Sistema'}</p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm border">
                    <div className="flex items-center gap-2 mb-1">
                      <Receipt className="h-4 w-4 text-gray-600" />
                      <span className="text-xs font-medium text-gray-500">Referencia</span>
                    </div>
                    <p className="text-sm font-medium text-gray-900">
                      {detallePago.numeroComprobante || '-'}
                    </p>
                  </div>

                  <div className="bg-white rounded-lg p-4 shadow-sm border">
                    <div className="flex items-center gap-2 mb-1">
                      <Building2 className="h-4 w-4 text-gray-600" />
                      <span className="text-xs font-medium text-gray-500">Banco</span>
                    </div>
                    <p className="text-base font-medium text-gray-900">
                      {detallePago.bancoNombre || detallePago.banco || '-'}
                    </p>
                  </div>
                </div>
              </div>

              {/* Facturas */}
              <div className="p-6 border-b">
                <h4 className="text-sm font-semibold text-gray-700 mb-3 flex items-center gap-2">
                  <FileText className="h-4 w-4" />
                  {detallePago?.medioPago === 'nota_credito' ? 'Factura Cancelada con NC' : 'Facturas Pagadas'}
                </h4>

                {detallePago.facturas?.length > 0 ? (
                  <div className="bg-gray-50 rounded-lg border overflow-hidden">
                    <table className="w-full text-sm">
                      <thead className="bg-gray-100">
                        <tr>
                          <th className="px-4 py-2 text-left font-medium text-gray-600">Factura</th>
                          <th className="px-4 py-2 text-left font-medium text-gray-600">Fecha</th>
                          <th className="px-4 py-2 text-right font-medium text-gray-600">Total</th>
                          <th className="px-4 py-2 text-right font-medium text-gray-600">Aplicado</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-gray-200">
                        {detallePago.facturas.map((f, idx) => (
                          <tr key={idx} className="bg-white">
                            <td className="px-4 py-2">
                              <div className="flex items-center gap-2">
                                <ChevronRight className="h-4 w-4 text-gray-400" />
                                <span className="font-mono font-medium">{f.numeroFactura}</span>
                                {f.facturaId && onVerFactura && (
                                  <button
                                    type="button"
                                    onClick={() => onVerFactura(f.facturaId)}
                                    className="p-1 text-gray-400 hover:text-primary-600 rounded"
                                    title="Ver factura (solo lectura)"
                                  >
                                    <Eye className="h-4 w-4" />
                                  </button>
                                )}
                              </div>
                            </td>
                            <td className="px-4 py-2 text-gray-500">{formatDate(f.fechaEmision)}</td>
                            <td className="px-4 py-2 text-right text-gray-700">
                              {formatMoney(f.totalFactura, detallePago.moneda)}
                            </td>
                            <td className="px-4 py-2 text-right font-semibold text-green-600">
                              {formatMoney(f.montoPagado, detallePago.moneda)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <div className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                    No hay detalle de facturas disponible
                  </div>
                )}
              </div>

              {/* Comprobante - Collapsible */}
              {detallePago.comprobanteStoragePath && comprobanteUrl ? (
                <div className="p-4 border-t">
                  <button
                    onClick={() => {
                      const el = document.getElementById('comprobante-content');
                      if (el) el.classList.toggle('hidden');
                    }}
                    className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 hover:text-gray-900"
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      Comprobante Adjunto
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <div id="comprobante-content" className="hidden mt-3">
                    {comprobanteType?.includes('pdf') ? (
                      <iframe src={`${comprobanteUrl}#toolbar=0`} className="w-full h-[50vh] rounded-lg border bg-white" />
                    ) : (
                      <img src={comprobanteUrl} alt="Comprobante" className="max-w-full max-h-[50vh] rounded-lg shadow-lg" />
                    )}
                  </div>
                </div>
              ) : detallePago?.medioPago === 'nota_credito' && detallePago?.numeroComprobante ? (
                <div className="p-4 border-t">
                  <button
                    onClick={() => {
                      const el = document.getElementById('nc-pdf-content');
                      if (el) el.classList.toggle('hidden');
                      // Load NC PDF if not loaded
                      if (!document.getElementById('nc-pdf-iframe')?.src) {
                        const ncConsecutivo = detallePago.numeroComprobante.replace('NC-', '');
                        gastosApi.listFacturas({ search: ncConsecutivo, limit: 1 }).then(res => {
                          const ncDoc = res.data?.data?.[0];
                          if (ncDoc) {
                            gastosApi.downloadPDF(ncDoc.id).then(pdfRes => {
                              const url = URL.createObjectURL(new Blob([pdfRes.data], { type: 'application/pdf' }));
                              const iframe = document.getElementById('nc-pdf-iframe');
                              if (iframe) iframe.src = `${url}#toolbar=0`;
                            }).catch(() => toast.error('Error al cargar PDF'));
                          }
                        }).catch(() => {});
                      }
                    }}
                    className="w-full flex items-center justify-between text-sm font-semibold text-gray-700 hover:text-gray-900"
                  >
                    <span className="flex items-center gap-2">
                      <FileText className="h-4 w-4" />
                      PDF de Nota de Crédito
                    </span>
                    <ChevronDown className="h-4 w-4" />
                  </button>
                  <div id="nc-pdf-content" className="hidden mt-3">
                    <iframe id="nc-pdf-iframe" className="w-full h-[50vh] rounded-lg border bg-white" />
                  </div>
                </div>
              ) : (
                <div className="p-4 border-t text-center text-gray-400 text-sm">
                  Sin comprobante adjunto
                </div>
              )}
            </>
          ) : (
            <div className="flex items-center justify-center h-64 text-gray-500">
              No se encontro el detalle
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="p-4 border-t flex justify-end">
          <button onClick={onClose} className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50 font-medium">
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}
